import { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useAnimationFrame } from '@playcastdotio/InputCapture';
import clsx from 'clsx';
import { getPeer, guestDecodedFrameCount, guestPresentationEvidence, guestRecoveryActionArbiter, playerDisconnect, requestGuestKeyframe, setRemoteVideoElement } from '../../api/modules/peer';
import {
  mediaStreamState,
  pauseInputState,
  playerInfoState,
  truncMachineIdState,
  playerConnectionState,
} from '../../api/stores/playerStores';
import { setTouchTargetElement } from '../../api/modules/hostMessaging/inputEvents';
import { resolveAudioPlaybackGrant } from './audioPlaybackGrant';
import { useTouchTargetRotationSync } from './useTouchTargetRotationSync';
import { useReconnectCoverLatch, useVideoFrameStallState } from './useVideoFrameStall';
import { useStallRecoveryEscalation } from './useStallRecoveryEscalation';
import { useStreamBounds } from './quickstart/useStreamBounds';
import { useWebCodecsSurface } from '../../features/video-playback/useWebCodecsSurface';
import { ReconnectingOverlay } from './quickstart/ReconnectingOverlay';
import type { StreamBounds } from '../../features/quickstart/types/theme';
import { useShouldShowMobileGamepad } from '@playcastdotio/SharedComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause } from '@fortawesome/duotone-thin-svg-icons/faPause';
import { generators } from '@playcastdotio/Messaging';
import { authStore } from '../../api/stores/globalStores';
import { captureException } from '@sentry/react';
import config from '../../config.json';
import { useIsPortraitOrientation, useShouldForceLandscape, useOrientationState } from '../../features/quickstart/utils/responsiveHooks';
import {
  reportForceLandscapeNotApplied,
  reportOrientationMismatch,
} from '../../features/quickstart/utils/orientationDiagnostics';
import { Z_STREAM, Z_STREAM_STATUS, Z_DEBUG } from '../../features/quickstart/config/zIndexLayers';
import { isIosSafariVideoGestureRequired } from './iosVideoPlaybackPolicy';
import { pageHidesStream } from './pauseStreamWhenPageHidden';
import { usePauseStreamWhenPageHidden } from './usePauseStreamWhenPageHidden';
import { PlayerWatchdogFaultLatch, requiresSrcObjectResetBeforeAttach } from './playerWatchdog';

const sendVideoDecodeTimingData = (stage: 'video_decode') => {
  const baseMessage = {
    header: {
      source: 'player' as const,
      target: 'system' as const,
      action: 'videoDecodeTiming' as const,
      isReply: false,
      hostId: '',
      userAuth: authStore.currentState.playerAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_${stage}`,
    },
    'player',
    {
      stage: stage,
      decode_timestamp: performance.now().toString(),
    },
  );

  console.log('[Player] Video decode timing data:', {
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

const sendVideoRenderTimingData = (stage: 'video_render') => {
  const baseMessage = {
    header: {
      source: 'player' as const,
      target: 'system' as const,
      action: 'videoRenderTiming' as const,
      isReply: false,
      hostId: '',
      userAuth: authStore.currentState.playerAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_${stage}`,
    },
    'player',
    {
      stage: stage,
      render_timestamp: performance.now().toString(),
    },
  );

  console.log('[Player] Video render timing data:', {
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};


type MachineIdPosition = 'top-left' | 'top-right';

interface PlayerContentProps {
  machineIdPosition?: MachineIdPosition;
  /** Called when the video element's rendered bounds change */
  onStreamBoundsChange?: (bounds: StreamBounds) => void;
  /** Coordinator ICE / onRetrying reconnect signal — ORed into the latched cover. */
  reconnectingSignal?: boolean;
  /**
   * When provided, Player owns latch math and reports visibility upward; the
   * stall ReconnectingOverlay is NOT mounted here (wrapper owns the single cover).
   */
  onReconnectCoverLatchedChange?: (latched: boolean) => void;
  /** When true with a latch callback, forces the cover clear (session left recovery). */
  reconnectCoverAbandoned?: boolean;
}

const PlayerContent = memo(({
  machineIdPosition = 'top-left',
  onStreamBoundsChange,
  reconnectingSignal = false,
  onReconnectCoverLatchedChange,
  reconnectCoverAbandoned = false,
}: PlayerContentProps) => {
  const playerId = getPeer()?.peerId;
  const [machineIdText, setMachineIdText] = useState('');

  // Touch-based mobile detection - detects touch as primary input
  const isMobileDevice = useShouldShowMobileGamepad();

  // Track user interactions with simple debouncing
  const lastUnmuteAttemptRef = useRef(0);
  const unmuteDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Autoplay permission is granted once per session by a user gesture; re-attaching
  // streams after an ICE recovery must not revoke it.
  const audioPlaybackGrantedRef = useRef(false);


  const [isFullscreen, setIsFullscreen] = useState(false);
  const isIdleRef = useRef(false);
  const [showIdleUI, setShowIdleUI] = useState(false);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoKickTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Add auto-kick timeout ref

  // The video's force-landscape rotation is driven primarily by `data-force-landscape`
  // + the CSS @media rules in styles.css (atomic with the viewport, no React in
  // the transform decision). We also emit an inline-style baseline mirroring the
  // CSS rule when `shouldForceLandscape && isPortraitOrientation` to defensively
  // cover Android Chrome cases where the @media rule fails to apply (containing-
  // block hijack, Tailwind specificity tie, dvh/dvw quirks). See styles.css.
  //   - `shouldForceLandscape` (stable per session) → drives the data attribute
  //     and the inline-style branch
  //   - `isPortraitOrientation` (reactive) → selects which inline-style branch
  //     to emit and powers the diagnostic
  const isPortraitOrientation = useIsPortraitOrientation();
  const shouldForceLandscape = useShouldForceLandscape();
  const { forceRotateDeg } = useOrientationState();

  // Diagnostic only — see orientationDiagnostics.ts. Fires once if the three
  // orientation signals disagree at the moment a force-rotation is being applied.
  const isVisuallyRotated = shouldForceLandscape && isPortraitOrientation;
  useEffect(() => {
    if (isVisuallyRotated) reportOrientationMismatch('Player.video');
  }, [isVisuallyRotated]);

  // Keep the touch input module in sync with the video's active CSS rotation so
  // fullscreen touches don't all map to the bottom-right corner (see the hook
  // for details). Tested in useTouchTargetRotationSync.test.ts.
  useTouchTargetRotationSync(isVisuallyRotated, forceRotateDeg);

  // Diagnostic only — verifies that when we expect a force-rotation, the video
  // actually picked up a non-`none` computed transform. If not, the CSS rule
  // failed to apply (cascade tie / containing-block hijack / dvh quirks); the
  // inline-style fallback in the JSX below normally rescues the visual outcome
  // but we still want to know about the cascade problem in production
  // telemetry. rAF-deferred so the report runs after layout has committed.
  useEffect(() => {
    if (!isVisuallyRotated) return;
    if (typeof window === 'undefined') return;
    const handle = window.requestAnimationFrame(() => {
      reportForceLandscapeNotApplied(videoFrame.current, 'Player.video');
    });
    return () => window.cancelAnimationFrame(handle);
  }, [isVisuallyRotated]);

  // Consolidate stream-related states into a single object
  const [streamState, setStreamState] = useState({
    hasAudio: false,
    hasVideo: false,
    isMuted: true,
    isPlaying: false,
    isVisible: true,
  });


  const videoFrame = useRef<HTMLVideoElement>(null);

  const streamBounds = useStreamBounds(videoFrame);
  useEffect(() => {
    onStreamBoundsChange?.(streamBounds);
  }, [streamBounds, onStreamBoundsChange]);

  const audioFrame = useRef<HTMLAudioElement>(null);
  usePauseStreamWhenPageHidden(videoFrame, audioFrame);
  const currentIsGamepadConnectedRef = useRef(false);

  // Callback ref to ensure touch target element is always set properly
  // This fixes the issue where touches map to (0,0) on mobile (PLAYCAST-1911)
  const setVideoFrameRef = useCallback((element: HTMLVideoElement | null) => {
    // Update the ref (TypeScript workaround for read-only .current)
    (videoFrame as React.MutableRefObject<HTMLVideoElement | null>).current = element;
    // Set touch target element whenever video element changes
    setTouchTargetElement(element);
    // Latency measurement reads frame-presentation times off this same element via
    // requestVideoFrameCallback. It belongs here rather than next to the srcObject assign:
    // the element identity is what the collector needs, and it survives stream swaps.
    setRemoteVideoElement(element);
  }, []);
  const previousStreamRef = useRef<{ stream: MediaStream | null; audioStream: MediaStream | null }>({ stream: null, audioStream: null });
  const previousVideoStreamCount = useRef(0);
  const previousStreamStateRef = useRef(streamState);
  const lastAppliedStreamRef = useRef<MediaStream | null>(null);

  // Latches the 1.5 s watchdog's fault reports so a persistent condition logs once per
  // transition into it instead of once per tick (and again if it recurs after clearing).
  const watchdogFaults = useRef(new PlayerWatchdogFaultLatch());

  const hasStreamUpdate = useRef(false);

  // Track consecutive pause state check failures for Sentry reporting
  const pauseCheckFailuresRef = useRef(0);
  const lastPauseCheckErrorRef = useRef<{ error: { name: string; message: string }; timestamp: number } | null>(null);

  const [newPlayers, setNewPlayers] = useState<Array<{ id: string; displayName: string; gamepadSlot: number }>>([]);
  const previousPlayersRef = useRef<Record<string, { displayName: string; gamepadSlots: number[] }>>({});

  // Register the video element as the touch target for relative coordinate computation
  // NOTE: Touch target element is now set via callback ref (setVideoFrameRef)
  // instead of useEffect to avoid React StrictMode cleanup issues (PLAYCAST-1911)

  const resetIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // Only update UI state if we're transitioning from idle to active
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setShowIdleUI(false);
    }

    idleTimeoutRef.current = setTimeout(() => {
      isIdleRef.current = true;
      if (isFullscreen) {
        setShowIdleUI(true);
      }
    }, 3000); // 3 seconds

  }, [isFullscreen]);

  const attemptUnmute = useCallback(() => {
    if (pageHidesStream(document.visibilityState)) return;
    const now = Date.now();
    const timeSinceLastAttempt = now - lastUnmuteAttemptRef.current;

    // Debounce to prevent spam (500ms minimum between attempts)
    if (timeSinceLastAttempt < 500) {
      console.log('🎯 Unmute attempt debounced');
      return;
    }

    lastUnmuteAttemptRef.current = now;

    try {
      const isAndroid = /Android/.test(navigator.userAgent);
      console.log('🎯 User interaction - checking and attempting to unmute');

      if (audioFrame.current && audioFrame.current.muted) {
        const audioStream = audioFrame.current.srcObject as MediaStream | null;

        if (audioStream && audioStream.getAudioTracks().length > 0) {
          const audioTracks = audioStream.getAudioTracks();
          console.log(
            '🎵 Attempting to unmute audio with tracks:',
            audioTracks.map((t) => ({
              id: t.id,
              label: t.label,
              enabled: t.enabled,
              readyState: t.readyState,
              muted: t.muted,
            })),
          );

          // For Android, refresh the stream connection
          if (isAndroid) {
            console.log('🤖 Android: Refreshing audio stream');
            const currentSrc = audioFrame.current.srcObject;
            audioFrame.current.srcObject = null;
            setTimeout(() => {
              if (audioFrame.current && currentSrc) {
                audioFrame.current.srcObject = currentSrc;
                audioFrame.current.muted = false;
                audioFrame.current.volume = 1.0;
                if (!pageHidesStream(document.visibilityState)) {
                  audioFrame.current.play().catch(() => undefined);
                }
              }
            }, 100);
          } else {
            audioFrame.current.muted = false;
            audioFrame.current.volume = 1.0;
            audioFrame.current.play().catch(() => undefined);
          }

          audioPlaybackGrantedRef.current = true;

          // Update stream state
          setStreamState((prev) => ({ ...prev, isMuted: false }));
          previousStreamStateRef.current = { ...previousStreamStateRef.current, isMuted: false };

          // Verify audio element state after unmute (especially for Quickstart)
          setTimeout(() => {
            if (audioFrame.current) {
              console.log('🔊 Audio unmuted - verification:', {
                isQuickstart: true,
                muted: audioFrame.current.muted,
                paused: audioFrame.current.paused,
                volume: audioFrame.current.volume,
                readyState: audioFrame.current.readyState,
                currentTime: audioFrame.current.currentTime,
                hasAudioTracks: (audioFrame.current.srcObject as MediaStream)?.getAudioTracks()?.length || 0,
              });
            }
          }, 200);
        } else {
          console.log('⏳ No audio tracks available yet');
        }
      } else {
        console.log('🔊 Audio already unmuted or no audio element');
      }
    } catch (error) {
      console.error('🎵 Error during unmute attempt:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // isQuickstartMode is constant prop, safe to omit from deps

  const attemptPlayVideo = useCallback(() => {
    // Try to play video if it's paused (for Android where video pauses unexpectedly)
    // This doesn't interfere with mobile gamepad since it only calls play() on the video element
    const video = videoFrame.current;
    if (pageHidesStream(document.visibilityState)) return;
    if (video && video.paused && streamState.isVisible) {
      const isAndroid = /Android/.test(navigator.userAgent);
      console.log('🎯 User interaction - attempting to play paused video', {
        isAndroid,
        readyState: video.readyState,
        networkState: video.networkState,
      });

      video.play()
        .then(() => {
          console.log('✅ Video play triggered by user interaction');
        })
        .catch((error) => {
          console.log('⚠️ Video play failed on user interaction:', error.name, error.message);
        });
    }
  }, [streamState.isVisible]);

  const onFirstInteraction = useCallback(() => {
    attemptUnmute();
    attemptPlayVideo();
  }, [attemptUnmute, attemptPlayVideo]);

  const handleGamepadCheck = useCallback(() => {
    const g = navigator.getGamepads();
    const hasGamepad = !!g.some((element) => element instanceof Gamepad);

    // Only update state if the value has changed
    if (hasGamepad !== currentIsGamepadConnectedRef.current) {
      currentIsGamepadConnectedRef.current = hasGamepad;
    }

    // Check for gamepad activity
    if (hasGamepad) {
      const gamepad = g[0];
      if (gamepad) {
        // Check if any button is pressed or any axis is moved
        const hasButtonPress = gamepad.buttons.some((button) => button.pressed);
        const hasAxisMovement = gamepad.axes.some((axis) => Math.abs(axis) > 0.1);
        if (hasButtonPress || hasAxisMovement) {
          resetIdleTimeout();
        }
      }
    }
  }, [resetIdleTimeout]);

  useAnimationFrame(handleGamepadCheck);

  const updateStreamState = useCallback((stream: MediaStream | null, audioStream: MediaStream | null) => {
    // Check if streams have actually changed

    if (stream) {
      const videoTracks = stream.getVideoTracks();

      if (
        previousStreamRef.current.stream === stream &&
        previousStreamRef.current.audioStream === audioStream &&
        previousVideoStreamCount.current === videoTracks.length
      ) {
        return;
      }

      // Update previous stream ref
      previousStreamRef.current = { stream, audioStream };
      previousVideoStreamCount.current = videoTracks.length;

      // Audio can arrive either on its own stream or bundled into the primary stream, and
      // the setup below already falls back to `stream`. Looking only at audioStream here
      // reported hasAudio=false for bundled audio and let the stale-audio branch clear it.
      const audioTracks = (audioStream ?? stream).getAudioTracks();

      const isVisible = !hasStreamUpdate.current || videoTracks.length > 0;
      hasStreamUpdate.current = true;

      audioPlaybackGrantedRef.current = resolveAudioPlaybackGrant(
        audioPlaybackGrantedRef.current,
        audioFrame.current ? audioFrame.current.muted : null,
      );
      const isMuted = !audioPlaybackGrantedRef.current;

      const newState = {
        hasVideo: videoTracks.length > 0,
        hasAudio: audioTracks.length > 0,
        isVisible: isVisible,
        isMuted,
        isPlaying: true, // Always true since video autoplays
      };

      // Only update if state has actually changed
      if (JSON.stringify(previousStreamStateRef.current) !== JSON.stringify(newState)) {
        previousStreamStateRef.current = newState;
        setStreamState(newState);
      }

      if (videoFrame.current) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
        const isAndroid = /Android/.test(navigator.userAgent);

        try {
          // Same platform set as the watchdog reattach path below — see playerWatchdog.ts.
          if (requiresSrcObjectResetBeforeAttach({ isIOS, isAndroid }) && videoFrame.current.srcObject) {
            videoFrame.current.srcObject = null;
          }

          videoFrame.current.srcObject = stream;
          videoFrame.current.autoplay = true;
          videoFrame.current.playsInline = true;
          videoFrame.current.muted = true;
        } catch (err) {
          console.error('[Player] 🚨 Failed to assign srcObject in updateStreamState, retrying', err);
          try {
            videoFrame.current.srcObject = stream;
          } catch {
            console.error('[Player] 🚨 Retry failed — video element may be black');
          }
        }
      }

      if (audioFrame.current && audioTracks.length > 0) {
        // Set up audio element with audio stream
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
        const isAndroid = /Android/.test(navigator.userAgent);

        // For mobile devices (iOS/Android), ensure we're setting srcObject with a clean approach
        if ((isIOS || isAndroid) && audioFrame.current.srcObject) {
          audioFrame.current.srcObject = null;
        }

        // Use the dedicated audioStream if available, otherwise use the main stream
        const audioStreamToUse = audioStream || stream;
        if (audioStreamToUse && audioStreamToUse.getAudioTracks().length > 0) {
          const audioTracks = audioStreamToUse.getAudioTracks();
          console.log('🎵 Setting up audio element with tracks (supports multiple):', {
            trackCount: audioTracks.length,
            tracks: audioTracks.map((t) => ({
              id: t.id,
              label: t.label,
              enabled: t.enabled,
              readyState: t.readyState,
              kind: t.kind,
            })),
          });

          // Clear previous srcObject and event listeners to prevent conflicts
          if (audioFrame.current.srcObject) {
            console.log('🗑️ Clearing previous audio srcObject');
            audioFrame.current.srcObject = null;
            audioFrame.current.onplay = null;
            audioFrame.current.onpause = null;
            audioFrame.current.onerror = null;
            audioFrame.current.oncanplay = null;
            audioFrame.current.onloadedmetadata = null;
          }

          audioFrame.current.srcObject = audioStreamToUse;
          audioFrame.current.autoplay = true;
          audioFrame.current.muted = isMuted; // Muted until a gesture grants autoplay
          audioFrame.current.volume = 1.0;
          if (!isMuted && !pageHidesStream(document.visibilityState)) {
            audioFrame.current.play().catch(() => undefined);
          }

          // Add event listeners for audio playback state
          audioFrame.current.onplay = () => {
            console.log('🎵 Audio started playing');
          };
          audioFrame.current.onpause = () => {
            console.log('⏸️ Audio paused');
          };
          audioFrame.current.onerror = (e) => {
            console.warn('❌ Audio playback error:', e);
          };

          // Simple audio monitoring
          audioFrame.current.oncanplay = () => {
            console.log('🎵 Audio can play - ready for playback');
          };

          audioFrame.current.onplay = () => {
            console.log('🎵 Audio started playing');
          };

          audioFrame.current.onpause = () => {
            console.log('⏸️ Audio paused');
          };

          audioFrame.current.onloadedmetadata = () => {
            console.log('🎵 Audio metadata loaded');
          };

          // For Android specifically, try to handle track changes more gracefully
          if (isAndroid) {
            // Add a small delay to ensure proper stream attachment on Android
            setTimeout(() => {
              if (!pageHidesStream(document.visibilityState) && audioFrame.current && audioFrame.current.paused) {
                console.log('🤖 Android: Attempting delayed audio play');
                audioFrame.current.play().catch(() => undefined);
              }
            }, 100);
          }
        } else {
          console.log('🚫 No audio tracks to set up, clearing audio element');
          if (audioFrame.current.srcObject) {
            audioFrame.current.srcObject = null;
          }
        }
      } else if (audioFrame.current && audioFrame.current.srcObject) {
        // Clear stale audio when no audio tracks are present (e.g., on reconnect/media reset)
        console.log('🗑️ Clearing stale audio element - no audio tracks');
        audioFrame.current.srcObject = null;
      }
    } else {
      const newState = {
        hasVideo: false,
        hasAudio: false,
        isVisible: false,
        isPlaying: false,
        isMuted: true,
      };

      // Only update if state has actually changed
      const stateChanged =
        !previousStreamStateRef.current || previousStreamStateRef.current.hasVideo || previousStreamStateRef.current.hasAudio;

      if (stateChanged) {
        previousStreamStateRef.current = newState;
        setStreamState(newState);
      }
    }
  }, []); // Removed dependencies to prevent loops

  // Simplified stream handling without dependency loops
  useEffect(() => {
    let isDestroyed = false;

    // Check initial state when component mounts
    const currentState = mediaStreamState.currentState;
    if (currentState.stream || currentState.audioStream) {
      updateStreamState(currentState.stream, currentState.audioStream);
    }

    const listener = mediaStreamState.addListener((state) => {
      if (isDestroyed) return;

      const audioTracks = state.audioStream ? state.audioStream.getAudioTracks() : [];
      console.log('🔄 Media stream updated:', {
        hasStream: !!state.stream,
        hasAudioStream: !!state.audioStream,
        audioTrackCount: audioTracks.length,
        trackDetails: audioTracks.map((t) => ({
          id: t.id,
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState,
          muted: t.muted,
          kind: t.kind,
        })),
      });

      updateStreamState(state.stream, state.audioStream);
    });

    return () => {
      isDestroyed = true;
      mediaStreamState.removeListener(listener);

      // Clean up debounce timeout
      if (unmuteDebounceTimeoutRef.current) {
        clearTimeout(unmuteDebounceTimeoutRef.current);
        unmuteDebounceTimeoutRef.current = null;
      }
    };
  }, []); // No dependencies



  useEffect(() => {
    const tryPlayMuted = async () => {
      try {
        if (pageHidesStream(document.visibilityState)) return;
        if (videoFrame.current) {
          const iosGestureRequired = isIosSafariVideoGestureRequired(
            navigator.userAgent,
            'ontouchend' in document,
          );
          if (iosGestureRequired) {
            videoFrame.current.muted = true;
          }
          await videoFrame.current.play().catch(() => undefined);
        }
        if (audioFrame.current && !pageHidesStream(document.visibilityState)) {
          await audioFrame.current.play().catch(() => undefined);
        }
      } catch {
        // ignore
      }
    };

    // Functions now defined as useCallback above

    // Attempt initial autoplay with muted media
    tryPlayMuted();

    // One-time gesture listeners with proper cleanup
    const handleInteraction = () => {
      onFirstInteraction();
    };

    // Use { once: true } to automatically remove listeners and prevent memory leaks
    window.addEventListener('pointerdown', handleInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    window.addEventListener('click', handleInteraction, { once: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { once: true, passive: true });

    return () => {
      // Cleanup in case component unmounts before first interaction
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []); // No dependencies needed

  useEffect(() => {
    if (streamState.isVisible) {
      videoFrame.current?.style.setProperty('display', 'block');
      // videoFrame.current?.play();
    } else {
      videoFrame.current?.style.setProperty('display', 'none');
      // videoFrame.current?.pause();
    }
  }, [streamState.isVisible]);

  // Periodically ensure the current MediaStream is applied to the video element and playing
  useEffect(() => {
    const intervalMs = 1500;

    // Helper to retry video.play() with exponential backoff.
    // `shouldLog` only suppresses console output for a pause we have already reported
    // (see PlayerWatchdogFaultLatch) — every retry, backoff and early-out still runs.
    const retryVideoPlay = async (video: HTMLVideoElement, maxRetries = 3, shouldLog = true) => {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (pageHidesStream(document.visibilityState)) return;
        try {
          if (shouldLog) {
            console.log(`🎬 Attempting to resume paused video (attempt ${attempt + 1}/${maxRetries})`, {
              paused: video.paused,
              readyState: video.readyState,
              networkState: video.networkState,
              currentTime: video.currentTime,
              timestamp: Date.now()
            });
          }

          await video.play();

          if (pageHidesStream(document.visibilityState)) return;
          if (shouldLog) {
            console.log('✅ Video resumed successfully', {
              paused: video.paused,
              readyState: video.readyState,
              currentTime: video.currentTime,
              timestamp: Date.now(),
              attempts: attempt + 1
            });
          }
          return; // Success - exit retry loop

        } catch (error: any) {
          if (pageHidesStream(document.visibilityState)) return;
          const isLastAttempt = attempt === maxRetries - 1;

          if (shouldLog) {
            console.warn(`${isLastAttempt ? '🚨' : '⚠️'} Video play() failed (attempt ${attempt + 1}/${maxRetries}):`, {
              errorName: error.name,
              errorMessage: error.message,
              paused: video.paused,
              readyState: video.readyState,
              networkState: video.networkState,
              srcObject: !!video.srcObject,
              timestamp: Date.now()
            });
          }

          // Don't retry certain errors (user aborted, etc)
          if (error.name === 'AbortError' || error.name === 'NotSupportedError') {
            if (shouldLog) console.log('🛑 Non-retryable error, stopping retry attempts');
            return;
          }

          // If not last attempt, wait with exponential backoff before retry
          if (!isLastAttempt) {
            const backoffMs = Math.min(100 * Math.pow(2, attempt), 1000); // 100ms, 200ms, 400ms, capped at 1s
            if (shouldLog) console.log(`⏳ Waiting ${backoffMs}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
          }
        }
      }
    };

    const tick = () => {
      const video = videoFrame.current;
      const audio = audioFrame.current;
      const stream = mediaStreamState.currentState.stream;
      const audioStream = mediaStreamState.currentState.audioStream;

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      const isAndroid = /Android/.test(navigator.userAgent);

      // Handle video element
      if (video && stream) {
        const videoTracks = stream.getVideoTracks();
        const hasVideo = videoTracks.length > 0;

        // Detect dead video tracks (ended server-side while audio continues).
        // Latched: a track that stays ended is a persistent condition, and this tick
        // runs every 1.5 s for the life of the stream.
        const endedTracks = hasVideo ? videoTracks.filter((vt) => vt.readyState === 'ended') : [];
        if (watchdogFaults.current.observe('videoTrackEnded', endedTracks.length > 0)) {
          console.error('[Player] 🚨 Video track is ENDED — stream will be black', {
            endedTrackCount: endedTracks.length,
            trackId: endedTracks[0].id,
            label: endedTracks[0].label,
            muted: endedTracks[0].muted,
            srcObjectPresent: !!video.srcObject,
            audioTracksAlive: audioStream?.getAudioTracks().filter(t => t.readyState === 'live').length ?? 0,
            timestamp: new Date().toISOString(),
          });
        }

        // Detect null srcObject (video element lost its stream)
        if (watchdogFaults.current.observe('videoSrcObjectNull', hasVideo && !video.srcObject)) {
          console.error('[Player] 🚨 Video srcObject is null despite having video tracks — recovering', {
            streamTrackCount: videoTracks.length,
            lastAppliedStream: !!lastAppliedStreamRef.current,
            timestamp: new Date().toISOString(),
          });
        }

        if (hasVideo) {
          // Re-apply stream if missing or changed
          if (video.srcObject !== stream || lastAppliedStreamRef.current !== stream) {
            try {
              // Same platform set as the primary attach path — see playerWatchdog.ts.
              if (requiresSrcObjectResetBeforeAttach({ isIOS, isAndroid }) && video.srcObject) {
                video.srcObject = null;
              }
              video.srcObject = stream;
              video.autoplay = true;
              video.playsInline = true;
              video.muted = true;
              lastAppliedStreamRef.current = stream;
            } catch (err) {
              console.error('[Player] 🚨 Failed to assign srcObject, attempting recovery', err);
              try {
                const fallbackStream = mediaStreamState.currentState.stream;
                if (fallbackStream) {
                  video.srcObject = fallbackStream;
                  lastAppliedStreamRef.current = fallbackStream;
                }
              } catch {
                console.error('[Player] 🚨 Recovery also failed — video will remain black');
              }
            }
          }

          // Only try to resume if the video is actually paused (not just buffering).
          // The retry always runs; only its logging is latched to the transition into
          // the paused-but-should-play condition.
          const pausedWhileVisible = !pageHidesStream(document.visibilityState) && streamState.isVisible && video.paused;
          const logPauseRetry = watchdogFaults.current.observe('videoPausedWhileVisible', pausedWhileVisible);
          if (pausedWhileVisible) {
            if (isIOS && !video.muted) {
              return;
            }
            // Use retry logic with more attempts on Android where pausing is more common
            const maxRetries = isAndroid ? 5 : 3;
            retryVideoPlay(video, maxRetries, logPauseRetry).catch(() => {
              // Final catch to ensure we never throw from async function
            });
          }
        }
      }

      // Handle audio element - especially important for Android
      if (audio && audioStream) {
        const hasAudio = audioStream.getAudioTracks().length > 0;
        if (hasAudio) {
          // Check if audio stream has changed and needs reattachment
          if (audio.srcObject !== audioStream) {
            console.log('🔄 Audio stream changed, reattaching to element');
            console.log('  Previous srcObject:', audio.srcObject ? 'present' : 'missing');
            console.log(
              '  New audioStream tracks:',
              audioStream.getAudioTracks().map((t) => ({ id: t.id, label: t.label, enabled: t.enabled })),
            );

            audio.srcObject = audioStream;
            // Preserve mute state from streamState, not from previous audio element
            audio.muted = streamState.isMuted;
            audio.volume = 1.0;

            if (!pageHidesStream(document.visibilityState) && !streamState.isMuted) {
              console.log('🔊 Audio should be unmuted, attempting to play');
              audio.play().catch(() => undefined);
            }
          }

          // Ensure audio is playing when it should be (especially important for Android and Quickstart)
          if (!pageHidesStream(document.visibilityState) && !streamState.isMuted && audio.paused && audio.readyState >= 2) {
            console.log(`${isAndroid ? '🤖 Android' : '🔊'}: Audio should be playing but is paused, attempting to resume`, {
              isQuickstart: true,
              readyState: audio.readyState,
              hasAudioTracks: audioStream.getAudioTracks().length,
              currentTime: audio.currentTime,
            });
            audio.play().catch((err) => {
              if (pageHidesStream(document.visibilityState)) return;
              console.warn('⚠️ Failed to resume audio:', err.name, err.message);
            });
          }

          // Double-check mute state synchronization
          if (audio.muted !== streamState.isMuted) {
            console.log(`${isAndroid ? '🤖 Android' : '🔊'}: Mute state desync detected, fixing...`, {
              audioElementMuted: audio.muted,
              streamStateMuted: streamState.isMuted,
              isQuickstart: true,
            });
            audio.muted = streamState.isMuted;

            // If we just unmuted, ensure audio is playing
            if (!pageHidesStream(document.visibilityState) && !streamState.isMuted && audio.readyState >= 2) {
              audio.play().catch(() => undefined);
            }
          }
        }
      }
    };

    const id = setInterval(tick, intervalMs);
    // Run once immediately to avoid waiting for the first interval
    tick();
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState.isVisible, streamState.isMuted]); // isQuickstartMode is constant prop, safe to omit from deps

  const handleExitStream = () => {
    playerDisconnect();
  };

  const checkFullscreen = useCallback(() => {
    const isNowFullscreen = !!document.fullscreenElement;
    setIsFullscreen(isNowFullscreen);

    if (isNowFullscreen) {
      resetIdleTimeout();
    }
  }, [resetIdleTimeout]);

  const handleFullscreenChange = useCallback(() => {
    checkFullscreen();
  }, [checkFullscreen]);


  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Events that count as valid user gestures for unmuting/playing media
    const validGestureEvents = [
      'mousedown',
      'pointerdown',
      'click',
      'dblclick',
      'keypress',
      'keydown',
      'keyup',
      'touchstart',
      'touchend',
      'input',
    ];

    // Events that only count for idle tracking (not valid for unmute)
    const passiveActivityEvents = [
      'mousemove',
      'pointermove',
      'touchmove',
      'scroll',
      'wheel',
      'focus',
    ];

    // Handler for valid gestures - can trigger unmute and play
    const handleValidGesture = (e: Event) => {
      resetIdleTimeout();

      // Try to unmute on valid user gestures with debouncing
      if (videoFrame.current?.muted || audioFrame.current?.muted) {
        console.log('🎯 Valid user gesture detected, checking if unmute needed', { type: e.type });

        // Clear existing timeout
        if (unmuteDebounceTimeoutRef.current) {
          clearTimeout(unmuteDebounceTimeoutRef.current);
        }

        // Debounced unmute attempt
        unmuteDebounceTimeoutRef.current = setTimeout(() => {
          attemptUnmute();
          unmuteDebounceTimeoutRef.current = null;
        }, 100); // Short debounce to prevent spam but still responsive
      }

      // ALSO try to play video if paused (important for Android)
      // This is separate from unmute logic and doesn't interfere with gamepad
      attemptPlayVideo();
    };

    // Handler for passive activity - only resets idle timeout
    const handlePassiveActivity = () => {
      resetIdleTimeout();
    };

    // Add valid gesture listeners
    validGestureEvents.forEach((eventType) => {
      window.addEventListener(eventType, handleValidGesture, {
        passive: true,
        capture: false,
      });
    });

    // Add passive activity listeners (no unmute attempts)
    passiveActivityEvents.forEach((eventType) => {
      window.addEventListener(eventType, handlePassiveActivity, {
        passive: true,
        capture: false,
      });
    });

    // Initial fullscreen check
    checkFullscreen();
    resetIdleTimeout();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);

      // Clean up all listeners
      validGestureEvents.forEach((eventType) => {
        window.removeEventListener(eventType, handleValidGesture);
      });

      passiveActivityEvents.forEach((eventType) => {
        window.removeEventListener(eventType, handlePassiveActivity);
      });

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      // Clean up auto-kick timeout
      if (autoKickTimeoutRef.current) {
        clearTimeout(autoKickTimeoutRef.current);
        autoKickTimeoutRef.current = null;
      }

      // Clean up unmute debounce timeout on unmount
      if (unmuteDebounceTimeoutRef.current) {
        clearTimeout(unmuteDebounceTimeoutRef.current);
        unmuteDebounceTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally run once on mount - callbacks are stable via useCallback

  // Video pause state monitoring - checks every 1 second if video is unexpectedly paused
  useEffect(() => {
    const pauseCheckInterval = setInterval(() => {
      if (pageHidesStream(document.visibilityState)) return;
      const video = videoFrame.current;

      // Only check if we have a video element, stream is visible, and video is paused
      if (!video || !streamState.isVisible || !video.paused) {
        // Reset failure counter if video is playing normally
        if (!video?.paused && pauseCheckFailuresRef.current > 0) {
          console.log('✅ Video resumed successfully, resetting failure counter');
          pauseCheckFailuresRef.current = 0;
          lastPauseCheckErrorRef.current = null;
        }
        return;
      }

      // Video is paused when it should be playing
      const videoState = {
        paused: video.paused,
        readyState: video.readyState,
        networkState: video.networkState,
        ended: video.ended,
        seeking: video.seeking,
        muted: video.muted,
        srcObject: !!video.srcObject,
        hasVideoTracks: (video.srcObject as MediaStream)?.getVideoTracks?.()?.length || 0,
        hasAudioTracks: (video.srcObject as MediaStream)?.getAudioTracks?.()?.length || 0,
        videoTrackState: (video.srcObject as MediaStream)?.getVideoTracks?.()?.[0]?.readyState || 'N/A',
      };

      console.log('⚠️ Video pause detected during monitoring check', videoState);

      // Try to play the video
      video
        .play()
        .then(() => {
          if (pageHidesStream(document.visibilityState)) return;
          console.log('✅ Video successfully resumed from pause check interval');
          pauseCheckFailuresRef.current = 0;
          lastPauseCheckErrorRef.current = null;
        })
        .catch((error) => {
          if (pageHidesStream(document.visibilityState)) return;
          pauseCheckFailuresRef.current++;
          const errorInfo = {
            error: {
              name: error.name,
              message: error.message,
            },
            timestamp: Date.now(),
          };
          lastPauseCheckErrorRef.current = errorInfo;

          console.error(
            `❌ Failed to resume video (attempt ${pauseCheckFailuresRef.current}):`,
            error.name,
            error.message,
            videoState
          );

          // Log to Sentry if we've had consecutive failures (threshold: 3 consecutive failures)
          if (pauseCheckFailuresRef.current >= 3) {
            const sentryContext = {
              videoState,
              errorInfo,
              failureCount: pauseCheckFailuresRef.current,
              userAgent: navigator.userAgent,
              isAndroid: /Android/.test(navigator.userAgent),
              isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent),
            };

            console.error('🚨 Logging video pause failure to Sentry', sentryContext);

            captureException(new Error('Video cannot be unpaused - invalid track or playback issue'), {
              level: 'warning',
              tags: {
                component: 'Player',
                issue_type: 'video_pause_failure',
                error_name: error.name,
              },
              extra: sentryContext,
            });

            // Reset counter after logging to avoid spam (will re-log if issue persists)
            pauseCheckFailuresRef.current = 0;
          }
        });
    }, 1000); // Check every 1 second

    return () => {
      clearInterval(pauseCheckInterval);
    };
  }, [streamState.isVisible]);

  // Add this effect to track new players
  useEffect(() => {
    const currentPlayers = playerInfoState.currentState.players;
    const previousPlayers = previousPlayersRef.current;

    Object.entries(currentPlayers).forEach(([playerId, playerInfo]) => {
      const prevInfo = previousPlayers[playerId];
      const prevSlots = prevInfo?.gamepadSlots ?? [];
      const currSlots = playerInfo.gamepadSlots ?? [];

      // Show indicator if slots transitioned from empty to non-empty
      if (prevSlots.length === 0 && currSlots.length > 0) {
        setNewPlayers((prev) => {
          if (prev.some((p) => p.id === playerId)) return prev;
          return [
            ...prev,
            {
              id: playerId,
              displayName: playerInfo.displayName,
              gamepadSlot: currSlots[0],
            },
          ];
        });
      }
    });

    previousPlayersRef.current = currentPlayers;
  }, [playerInfoState.currentState.players]);

  // Add cleanup for newPlayers
  useEffect(() => {
    const timer = setTimeout(() => {
      setNewPlayers([]);
    }, 5000);

    return () => clearTimeout(timer);
  }, [newPlayers]);

  // Add debug log for newPlayers state
  useEffect(() => {
    console.log('New Players State:', newPlayers);
  }, [newPlayers]);

  useEffect(() => {
    if(truncMachineIdState.currentState) setMachineIdText(truncMachineIdState.currentState);
    const listener = truncMachineIdState.addListener((state) => setMachineIdText(state));
    return () => { truncMachineIdState.removeListener(listener) }
  }, []);

  // Frame-stall watchdog: raise the "recovering…" cover the moment inbound video
  // frames stop advancing, closing the gap before PeerCore's ICE-disconnect
  // detection flips the coordinator's reconnecting state. Only armed while we
  // believe an active video stream should be painting, so intentional
  // pause/stop/teardown never trips it.
  // With WebCodecs selected the <video> legitimately stops advancing (the host suspends
  // conventional video), so the stall watchdog must stand down or it would rebuild the
  // peer stack under a healthy session.
  const presentationMode = useWebCodecsSurface(videoFrame);
  const stallState = useVideoFrameStallState({
    videoRef: videoFrame,
    enabled: streamState.hasVideo && streamState.isVisible && presentationMode !== 'webcodecs',
    // Decoder progress from WebRTC stats, so a silent rVFC on Android's hardware-overlay
    // path cannot be mistaken for a frozen picture.
    frameProgressProbe: guestDecodedFrameCount,
    evidenceProbe: guestPresentationEvidence,
  });
  const framesStalled = stallState.state === 'stalled';

  const [peerStatus, setPeerStatus] = useState(playerConnectionState.currentState.status);
  useEffect(() => {
    const id = playerConnectionState.addListener((state) => setPeerStatus(state.status));
    return () => playerConnectionState.removeListener(id);
  }, []);

  // Keyframe at 1.5s of stall. The peer stays up: a frozen picture on a live
  // connection is waiting on a reference frame, and a new session does not repair it.
  useStallRecoveryEscalation({
    framesStalled,
    stallState,
    actionArbiter: guestRecoveryActionArbiter(),
    peerConnected: peerStatus === 'connected',
    requestKeyframe: requestGuestKeyframe,
    onEscalate: (step, stalledForMs) => {
      console.warn('[Player] Stall recovery escalation', { step, stalledForMs });
    },
  });

  // Single latched cover ownership lives in Quickstart/Beacon wrappers when a
  // change callback is provided — Player only feeds stall + hold into the latch.
  const ownsLatchedCover = typeof onReconnectCoverLatchedChange === 'function';
  const peerReconnecting = peerStatus === 'reconnecting';
  const reconnectCoverLatched = useReconnectCoverLatch({
    videoRef: videoFrame,
    enabled: ownsLatchedCover,
    reconnecting: reconnectingSignal || peerReconnecting,
    framesStalled,
    peerConnected: peerStatus === 'connected',
    abandoned: reconnectCoverAbandoned,
  });

  useEffect(() => {
    if (!ownsLatchedCover) return;
    onReconnectCoverLatchedChange?.(reconnectCoverLatched);
  }, [ownsLatchedCover, reconnectCoverLatched, onReconnectCoverLatchedChange]);

  return (
    <>
      <div className='absolute flex h-dvh w-full overflow-hidden'>
        {(pauseInputState.currentState.paused || pauseInputState.currentState.players[playerId ?? '']?.paused) && (
          <div className='fixed left-1/2 top-0 mt-4 -translate-x-1/2' style={{ zIndex: Z_STREAM_STATUS }}>
            <FontAwesomeIcon
              icon={faPause}
              className='size-16 text-amber-600'
              style={
                {
                  '--fa-secondary-color': '#ffba00',
                  '--fa-secondary-opacity': '1',
                } as React.CSSProperties
              }
            />
          </div>
        )}
      </div>
      <div className='absolute flex h-dvh w-full items-end overflow-hidden'>
        {/* Custom ESC notification */}
        {isFullscreen && showIdleUI && !isMobileDevice && (
          <div
            id='fullscreen-notification'
            data-testid='fullscreen-notification'
            className={clsx(
              'fixed left-1/2 top-0 mt-8 -translate-x-1/2',
              'transform-gpu',
              '[transform:translate(-50%,0)]',
              'animate-notification-slide-down',
              'bg-primary rounded-lg px-4 py-2',
              'shadow-lg shadow-black/10',
            )}
            style={{ zIndex: Z_STREAM_STATUS }}
          >
            <span id='fullscreen-notification-text' data-testid='fullscreen-notification-text' className='font-semibold text-gray-900'>
              Hold ESC to exit fullscreen
            </span>
          </div>
        )}
        <div
          className={clsx('h-[100dvh] w-[100vw]', 'bg-content absolute flex items-center justify-center', 'select-none [&_*]:select-none')}
          style={{
            WebkitTouchCallout: 'none',
            touchAction: 'none',
          } as React.CSSProperties}
        >
          {/* Always-on opaque backing directly behind the video (below the
              Z_STREAM z-10 layer). Guarantees a frameless/transparent <video>
              composites over solid gray-900 instead of the browser's default
              checkerboard, regardless of connection-state timing. It sits behind
              the video (opaque while painting frames) so healthy streams are
              unaffected. */}
          <div
            aria-hidden='true'
            data-testid='video-stream-backing'
            className='bg-content pointer-events-none absolute inset-0 z-0'
          />

          <video
            ref={setVideoFrameRef}
            // Rotation/sizing primarily lives in CSS via `data-force-landscape='true'`
            // + @media (orientation: portrait|landscape) rules in styles.css. The CSS
            // rule keeps switching atomic with the viewport, which is what eliminates
            // the iOS Safari double-rotation race (180° flip) that previous inline-
            // style implementations suffered from.
            //
            // When `shouldForceLandscape` is on, we ALSO emit an inline-style baseline
            // mirroring the CSS rule. This is a defensive fallback for Android Chrome
            // edge cases where the @media-driven rule fails to apply correctly:
            //   - a transformed/filtered ancestor can hijack the `position: fixed`
            //     containing block
            //   - bundled CSS source order can cause Tailwind utilities to win the
            //     specificity tie and override the rule
            //   - certain Android Chrome versions misreport `100dvh`/`100dvw` inside
            //     fixed/transform containing-block contexts
            // Both paths pick the same final transform/dimensions so the cascade
            // outcome is consistent — CSS wins when active, inline guarantees a
            // working baseline when CSS doesn't.
            data-force-landscape={shouldForceLandscape ? 'true' : undefined}
            className={clsx(
              'bg-content absolute z-10 object-contain',
              // `aspect-video` enforces a 16:9 element box. We only apply it
              // OUTSIDE force-vertical mode. Under force-vertical the element
              // is always sized explicitly (CSS rule + inline fallback both
              // set width/height), so `aspect-ratio` is layout-irrelevant on
              // the happy path. The reason we strip it is defense-in-depth:
              // if the orientation signal ever races to `false` and the
              // wrong-branch inline style applies (`width:100%, height:100%`
              // of the portrait parent), `aspect-video` is what collapses
              // the visible video media to the ~16:9 strip Hunter and Jen
              // observed on real-device Android Chrome. Without it, the
              // worst-case wrong-branch render is a full-bleed video — still
              // visibly broken if rotation fails entirely, but loud enough
              // to be reported immediately rather than mistaken for "force-
              // vertical just looks small".
              !shouldForceLandscape && 'aspect-video',
              streamState.isVisible ? 'visible' : 'hidden',
            )}
            style={
              shouldForceLandscape
                ? isPortraitOrientation
                  ? {
                      position: 'fixed',
                      top: '50%',
                      left: '50%',
                      width: '100vh',
                      height: '100vw',
                      maxWidth: '100dvh',
                      maxHeight: '100dvw',
                      // Reference the same `--force-rotate-angle` CSS variable
                      // the @media rule reads, so upside-down-portrait counter-
                      // rotation still works when the inline style wins the
                      // cascade against the CSS rule.
                      transform:
                        'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
                      transformOrigin: 'center center',
                    }
                  : {
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      maxWidth: '100vw',
                      maxHeight: '100vh',
                    }
                : {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                  }
            }
            hidden={!streamState.isVisible}
            autoPlay
            muted={true}
            playsInline
            webkitPlaysInline={true}
            x-webkit-airplay="deny"
            disablePictureInPicture
            controlsList="nofullscreen nodownload noremoteplayback"
            draggable={false}
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
            onLoadedData={() => {
              sendVideoDecodeTimingData('video_decode');
            }}
            onCanPlay={() => {
              sendVideoRenderTimingData('video_render');
            }}
            onPause={(e) => {
              const video = e.currentTarget;
              console.warn('🎬 VIDEO PAUSED EVENT', {
                timestamp: new Date().toISOString(),
                currentTime: video.currentTime,
                readyState: video.readyState,
                networkState: video.networkState,
                paused: video.paused,
                ended: video.ended,
                seeking: video.seeking,
                muted: video.muted,
                srcObject: !!video.srcObject,
                hasVideoTracks: (video.srcObject as MediaStream)?.getVideoTracks?.()?.length || 0,
                // No stack capture here: Android fires `pause` on backgrounding and on
                // audio-focus loss, and each captured stack is retained by the console.
              });
            }}
            onPlay={(e) => {
              const video = e.currentTarget;
              console.log('▶️ VIDEO PLAY EVENT', {
                timestamp: new Date().toISOString(),
                currentTime: video.currentTime,
                readyState: video.readyState,
                networkState: video.networkState,
                paused: video.paused,
                muted: video.muted
              });
            }}
          ></video>

          <div className={`absolute top-4 pointer-events-none ${machineIdPosition === 'top-right' ? 'right-8 -rotate-90 origin-top-right' : 'left-8 rotate-90 origin-top-left'}`} style={{ zIndex: Z_DEBUG }}>
            <span className="text-white/50 text-sm font-large select-none">{machineIdText}</span>
          </div>

          {/* Audio element - hidden but necessary for audio playback */}
          <audio
            ref={audioFrame}
            autoPlay
            muted={true}
            style={{ display: 'none' }}
          ></audio>

          {!streamState.isVisible && (
            // No host-provided static frame exists here: render an opaque
            // gray-900 fill rather than a broken <img> (the former
            // images/global-frame.png asset does not exist in public/).
            <div className='bg-content max-w-screen absolute z-[5] flex h-full max-h-screen w-full items-center justify-center' />
          )}

          {/* Legacy path only: local stall cover. Quickstart/Beacon wrappers own
              the single latched ReconnectingOverlay (5A). */}
          {!ownsLatchedCover && <ReconnectingOverlay visible={framesStalled} />}
        </div>
      </div>
    </>
  );
});

// Update Player to forward props
export const Player: React.FC<PlayerContentProps> = (props) => <PlayerContent {...props} />;

